import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import pagesEn from './locales/pages-en.json';
import pagesEs from './locales/pages-es.json';

/**
 * Deep-merge two translation objects. Later wins on leaf conflicts; nested
 * objects are merged recursively so both files can contribute partial schemas
 * under the same top-level key.
 *
 * Necessary because `en.json` and `pages-en.json` both define `howItWorksPage`,
 * `aboutPage`, `pricingPage` etc. with different sub-keys — a shallow spread
 * here would have one file silently clobber the other and any t() call against
 * the loser would render the literal key string.
 */
function deepMerge<T extends Record<string, any>>(a: T, b: T): T {
    const out: any = { ...a };
    for (const k of Object.keys(b)) {
        const av = (a as any)[k];
        const bv = (b as any)[k];
        if (av && bv && typeof av === 'object' && typeof bv === 'object' && !Array.isArray(av) && !Array.isArray(bv)) {
            out[k] = deepMerge(av, bv);
        } else {
            out[k] = bv;
        }
    }
    return out;
}

void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: deepMerge(en as any, pagesEn as any) },
            es: { translation: deepMerge(es as any, pagesEs as any) },
        },
        fallbackLng: 'en',
        supportedLngs: ['en', 'es'],
        interpolation: { escapeValue: false },
        detection: {
            order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
            lookupLocalStorage: 'i18nextLng',
            caches: ['localStorage'],
        },
    });

export default i18n;
